//* Importing necessary modules
import { useCallback } from "react";

//* Importing DOMPurify
import DOMPurify from "dompurify";

//! This file is not used in the project. It is a configuration file for the Jodit editor.
import { API_URI, STORAGE_URL } from "../constants/consts";

export interface MyEditorConfig {
  readonly?: boolean;
  toolbar?: boolean;
  height?: number;
  language?: string;
  buttons?: string;
  imageDefaultWidth?: number;
  imageResize?: boolean; 
  allowResizeTags?: string[];
  tabIndex?: number;
  askBeforePasteHTML?: boolean;
  askBeforePasteFromWord?: boolean;
  defaultActionOnPaste?: string;
  defaultActionOnPasteFromWord?: string;
  pasteHTMLActionList?: { value: string; text: string }[];
  pasteFromWordActionList?: { value: string; text: string }[];
  toolbarButtonSize?: "small" | "large";
  resizer?: {
    showSize: boolean;
    useAspectRatio: boolean;
  };
  uploader?: {
    url: string;
    insertImageAsBase64URI: boolean;
    imagesExtensions: string[];
    headers: { [key: string]: string };
    filesVariableName: () => string;
    withCredentials: boolean;
    pathVariableName: string;
    method: string;
    prepareData: (formData: FormData) => FormData;
    isSuccess: (response: any) => string;
    getMessage: (response: any) => any;
    process: (resp: any) => { files: string[] };
    error: (elem: any, error: any) => void;
    defaultHandlerSuccess: (resp: any, elem: any) => void;
    defaultHandlerError: (elem: any, error: any) => void;
  };
}

  function getToken() {
    return localStorage.getItem("token") || "";
  }

/**
 * Default configuration for the Jodit editor.
 * 
 * @constant
 * @type {MyEditorConfig}
 * @property {boolean} readonly - Determines if the editor is in read-only mode.
 * @property {boolean} toolbar - Determines if the toolbar is visible.
 * @property {number} height - Height of the editor in pixels.
 * @property {string} language - Language setting for the editor.
 * @property {number} imageDefaultWidth - Default width for images in the editor.
 * @property {boolean} imageResize - Determines if images can be resized.
 * @property {string} buttons - Comma-separated list of buttons to display in the toolbar.
 * @property {object} uploader - Configuration for the image uploader.
 * @property {string} uploader.url - URL to upload images.
 * @property {boolean} uploader.insertImageAsBase64URI - Determines if images should be inserted as Base64 URI.
 * @property {string[]} uploader.imagesExtensions - Allowed image file extensions.
 * @property {object} uploader.headers - Headers to include in the upload request.
 * @property {string} uploader.headers.accept - Accept header for the upload request.
 * @property {string} uploader.headers.Authorization - Authorization header for the upload request.
 * @property {function} uploader.filesVariableName - Function to get the variable name for files.
 * @property {boolean} uploader.withCredentials - Determines if credentials should be included in the upload request.
 * @property {string} uploader.pathVariableName - Variable name for the upload path.
 * @property {string} uploader.method - HTTP method for the upload request.
 * @property {function} uploader.prepareData - Function to prepare the FormData for the upload request.
 * @property {function} uploader.isSuccess - Function to determine if the upload was successful.
 * @property {function} uploader.getMessage - Function to get the message from the upload response.
 * @property {function} uploader.process - Function to process the upload response.
 * @property {function} uploader.error - Function to handle upload errors.
 * @property {function} uploader.defaultHandlerSuccess - Default success handler for the upload.
 * @property {function} uploader.defaultHandlerError - Default error handler for the upload.
 */
export const defaultEditorConfig = {
  height: 300,
  uploader: {
    url: `${API_URI}/admin/private/questions/file`,
    insertImageAsBase64URI: false,
    imagesExtensions: ["jpg", "png", "jpeg", "gif"],
    headers: {
      accept: "application/json",
      Authorization: `Bearer ${getToken()}`,
    },
    filesVariableName: () => "file",
    withCredentials: false,
    pathVariableName: "path",
    method: "POST",
    prepareData: (formData: FormData) => {
      return formData;
    },
    isSuccess: (response: any) => {
      return "Upload realizado com sucesso";
    },
    getMessage: (response: any) => {
      return response;
    },
    process: (resp: any) => {
      const files: string[] = [];
      files.unshift(`${STORAGE_URL}${resp.data}`);
      return { files };
    },
    error: (elem: any, error: any) => {},
    defaultHandlerSuccess: function (resp: any, elem: any) {
      const editor = this as any;
      if (resp.files && resp.files.length) {
        resp.files.forEach((filename: string) => {
          editor.selection.insertImage(filename);
        });
      }
    },
    defaultHandlerError: (elem: any, error: any) => {},
  },
  allowResizeTags: ["img", "iframe", "table", "jodit"],
  resizer: {
    showSize: true,
    useAspectRatio: true,
  },
  tabIndex: 1,
  askBeforePasteHTML: false,
  askBeforePasteFromWord: false,
  pasteHTMLActionList: [
    { value: "insert_only_text", text: "Insert only Text" },
  ],
  pasteFromWordActionList: [
    { value: "insert_only_text", text: "Insert only Text" },
  ],
  toolbarButtonSize: "large" as "large",
  readonly: false,
};



/**
 * Custom hook to sanitize input strings using DOMPurify.
 *
 * @returns An object containing the `sanitizeInput` function.
 *
 * @example
 * const { sanitizeInput } = useSanitizeInput();
 * const cleanString = sanitizeInput('<script>alert("xss")</script><b>bold</b>');
 * // cleanString will be '<b>bold</b>'
 */
export const useSanitizeInput = () => {
  const sanitizeInput = useCallback((input: string): string => {
    return DOMPurify.sanitize(input, {
      ALLOWED_TAGS: [
        "b",
        "i",
        "em",
        "strong",
        "u",
        "p",
        "br",
        "ul",
        "ol",
        "li",
        "span",
        "img",
      ],
    });
  }, []);

  return { sanitizeInput };
};
